import { RequestService } from "./requests";
import {
  AuditLogExport,
  CustomVerificationType,
  DeployContractArgs,
  Image,
  Organization,
  PaginationInfo,
  SKU,
  SKUAnalytics,
  Tag,
  TagAnalytics,
  TagVerification,
  Track,
  TransferTagsBody,
  Video,
  WorkOSInvitation,
  WorkOSOrganization,
  WorkOSOrganizationMembership,
  WorkOSPermission,
  WorkOSUser,
} from "./types";

const requests = new RequestService();

// SKUs
export const getSKUs = (params?: object) => {
  return requests.get<{ templates: SKU[]; pagy: PaginationInfo }>(
    "/templates",
    params,
  );
};

// non-paginated, only lists id and name
export const getSKUsForSKUSelection = (params?: object) => {
  return requests.get<{ templates: SKU[] }>("/templates?list=true", params);
};

export const getSKU = (skuId: string) => {
  return requests.get<SKU>(`/templates/${skuId}`);
};

export const getSKUAnalytics = (skuId: string, start: string, end: string) => {
  return requests.get<SKUAnalytics>(`/analytics/template/${skuId}`, {
    start_time: start,
    end_time: end,
  });
};

export const createSKU = (params: Partial<SKU>) => {
  return requests.post<SKU>("/templates", { template: params });
};

export const updateSKU = (skuId: string, params: Partial<SKU>) => {
  return requests.put<SKU>(`/templates/${skuId}`, { template: params });
};

export const deleteSKU = (skuId: string) => {
  return requests.delete(`/templates/${skuId}`);
};

export const transferSKU = (skuId: string, workOSOrgId: string) => {
  return requests.post(`templates/${skuId}/transfer`, {
    workos_org_id: workOSOrgId,
  });
};

export const resequenceSKU = (skuId: string) => {
  return requests.post(`templates/${skuId}/resequence`, {});
};

export const exportSKUs = () => {
  return requests.get<Blob>(
    "/templates/export_csv",
    {},
    { responseType: "blob" },
  );
};

// Tags
export const getTags = (params?: object) => {
  return requests.get<{ tags: Tag[]; pagy: PaginationInfo }>("/tags", params);
};

export const getTag = (tagUid: string) => {
  return requests.get<Tag>(`/tags/${tagUid}`);
};

export const getTagAnalytics = (uid: string, start: string, end: string) => {
  return requests.get<TagAnalytics>(`/analytics/tag/${uid}`, {
    start_time: start,
    end_time: end,
  });
};

export const deleteTag = (tagId: string) => {
  return requests.delete(`/tags/${tagId}`);
};

export const transferTags = (body: TransferTagsBody) => {
  return requests.post("/tags/transfer", {
    template_id: body.fromTemplateId,
    to_template_id: body.toTemplateId,
    starting_serial_number: body.startingSerial,
    ending_serial_number: body.endingSerial,
  });
};

export const exportTags = () => {
  return requests.get<Blob>("/tags/export_csv", {}, { responseType: "blob" });
};

// Verifications
export const getTagVerifications = (params?: object) => {
  return requests.get<{
    verifications: TagVerification[];
    pagy: PaginationInfo;
  }>("/verifications", params);
};

export const exportTagVerifications = () => {
  return requests.get<Blob>(
    "/verifications/export_csv",
    {},
    { responseType: "blob" },
  );
};

// Owners
export const exportOwners = (templateId: string) => {
  return requests.get<Blob>(
    `/owners/export_csv/${templateId}`,
    {},
    { responseType: "blob", headers: { "Content-Disposition": "attachment" } },
  );
};

// Organization
export const getOrganization = () => {
  return requests.get<{
    workos_org: WorkOSOrganization;
    organization: Organization;
    workos_org_users: WorkOSUser[];
  }>("/auth/organization/details");
};

export const getWidgetToken = (scopes?: WorkOSPermission[]) => {
  return requests.get<{ token: string }>("/auth/workos_widget_token", {
    scopes,
  });
};

export const getOrganizations = () => {
  return requests.get<Organization[]>("/organizations");
};

export const createOrganization = (name: string) => {
  return requests.post<Organization>("/organizations", { name });
};

export const deleteOrganization = (workOSOrganizationId: string) => {
  return requests.delete(`/organizations/${workOSOrganizationId}`);
};

export const getOrganizationMemberships = () => {
  return requests.get<WorkOSOrganizationMembership[]>(
    "/auth/organization-memberships",
  );
};

export const getOrganizationInvitations = () => {
  return requests.get<WorkOSInvitation[]>("/auth/invites");
};

// Contract
export const deployContract = ({ skuId, body }: DeployContractArgs) => {
  return requests.post<SKU>(`/templates/${skuId}/deploy`, {
    token_name: body.tokenName,
    token_symbol: body.tokenSymbol,
    nft_chain_id: body.nftChainId,
  });
};

export const forceMetadataUpdate = (skuId: string) => {
  return requests.post(`/templates/${skuId}/update-metadata`, {});
};

export const forceMint = (skuId: string) => {
  return requests.post(`/templates/${skuId}/mint`, {});
};

// Audit Logs

export const getAuditLogs = () => {
  return requests.get<AuditLogExport>("/auth/organization/logs/request");
};

export const downloadAuditLogs = (exportId: string) => {
  return requests.get<AuditLogExport>(`/auth/organization/logs/${exportId}`);
};

// Images

export const getImageUploadUrl = () => {
  return requests.get<{ id: string; uploadURL: string }>("/images/upload_url");
};

export const createImage = (image: Partial<Image>) => {
  return requests.post<Image>("/images", image);
};

export const getImages = (params?: object) => {
  return requests.get<{ images: Image[]; pagy: PaginationInfo }>(
    "/images",
    params,
  );
};

export const updateImage = (image: Partial<Image>) => {
  return requests.put<Image>(`/images/${image.id}`, { image });
};

export const deleteImage = (id: number) => {
  return requests.delete(`/images/${id}`);
};

// Videos

export const getVideoUploadUrl = () => {
  return requests.get<{ uid: string; uploadURL: string }>("/videos/upload_url");
};

export const createVideo = (video: Partial<Video>) => {
  return requests.post<Video>("/videos", video);
};

export const getVideo = (id: number) => {
  return requests.get<Video>(`/videos/${id}`);
};

export const getVideos = (params?: object) => {
  return requests.get<{ videos: Video[]; pagy: PaginationInfo }>(
    "/videos",
    params,
  );
};

export const updateVideo = (video: Partial<Video>) => {
  return requests.put<Video>(`/videos/${video.id}`, { video });
};

export const deleteVideo = (id: number) => {
  return requests.delete(`/videos/${id}`);
};

// Tracks
export const getTrackUploadUrl = (file_name: string) => {
  return requests.get<{
    file_name: string;
    object_key: string;
    uid: string;
    url: string;
  }>("/tracks/upload_url", {
    file_name: file_name,
  });
};

export const createTrack = (track: Partial<Track>) => {
  return requests.post<Track>("/tracks", track);
};

export const getTracks = (params?: object) => {
  return requests.get<{ tracks: Track[]; pagy: PaginationInfo }>(
    "/tracks",
    params,
  );
};

export const updateTrack = (track: Partial<Track>) => {
  return requests.put<Track>(`/tracks/${track.id}`, { track });
};

export const deleteTrack = (id: number) => {
  return requests.delete(`/tracks/${id}`);
};

// Custom Verification Types

export const getCustomVerificationTypes = () => {
  return requests.get<CustomVerificationType[]>("/custom_verification_types");
};

export const createCustomVerificationType = (
  body: Partial<CustomVerificationType>,
) => {
  return requests.post<CustomVerificationType>(
    "/custom_verification_types",
    body,
  );
};

export const deleteCustomVerificationType = (verificationTypeId: string) => {
  return requests.delete(`/custom_verification_types/${verificationTypeId}`);
};

export const getCustomVerificationsCount = (tagUid: string) => {
  return requests.get<Record<string, number>>(
    `/tags/${tagUid}/custom_verifications_count`,
  );
};
